import { createApi } from "@reduxjs/toolkit/query/react";
import { baseProxyQueryWithReauth, baseQueryWithErrorHandling } from "./API";
import { pageProps } from "../../helpers/pageProps";
import { string } from "prop-types";
import { uuid } from "short-uuid";
import { AssetsMapRequestType } from "./mapsSlice";

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase, csrfToken } = JSON.parse(
  el?.dataset?.page ?? ""
).props;

export type EventType = {
  Uuid: string;
  ClientId: number;
  Reference: string;
  Label: string;
  Description: string;
  Ticket: string;
  Status: "active" | "resolved" | "closed" | "archived" | "creating";
  Severity: "low" | "medium" | "high" | "critical";
  StartAt: string; // ISO date string
  EndAt: { Time: string; Valid: boolean }; // ISO date string or null
  CreatedAt: string; // ISO date string
  UpdatedAt: string; // ISO date string
  EventAssets: { AssetUuid: string }[]; // Array of asset UUIDs
};

export type CommsSettingsDataType = {
  ClientId: number;
  BroadcastSetting: "dynamic-header" | "single-ticket" | "open-ticket";
  IsBroadcastFixed: boolean;
  SmsHeader: string;
  EmailFrom: string;
  EmailSubject: string;
  EmailSignature: string;
  EmailImage: string;
  VoiceIntro: string;
  VoiceExit: string;
  VoiceRetries: number;
};

export type OrganisationDetailsType = {
  customer_profiles: {
    contact_name: string;
    email: string;
    phone: number;
    www: string;
    address: string;
    postcode: string;
    company_num: number;
    vat_number: number;
    invoicing_address: string;
    is_same_address: number;
    support_name: string;
    support_email: string;
    account_name: string;
    account_email: string;
  };
  customers: {
    id: number;
    country_id: number;
    name: string;
  };
};

export type ApiConnectionType = {
  Uuid: string;
  Label: string;
  Reference: string;
  Description: string;
  Kind: "nylas" | "third-party" | "logic";
  Status: "production";
  NylasEmail?: string;
  LastUsed?: string;
  UpdatedAt?: string;
  CreatedAt?: string;
  Token?: string;
  Triggers?: {
    ApiConnectionUuid: string;
    CallUuid: string;
    CallType: "component";
    Reference: string;
    JsonApiDataCollectionUuid: string;
    JsonApiDataCollection: null;
    JsonApiDataCollectionMap: null;
    Project: {
      ID: number;
      CreatedAt: string;
      UUID: string;
      Label: string;
      Description: string;
      Reference: string;
      Status: "production" | "draft" | "archived";
      CreatedBy: string;
      MessageConfigUUID: string;
      ClientID: number;
      Module: string;
      TimeZone: string;
      CountryISO: string;
      LastRunAt: string;
      IsSingleton: boolean;
    };
  }[];
  Profile: {
    Uuid: string;
    Label: string;
    ClientId: number;
    BaseUrl: string;
    AuthHeader: string;
    AuthValue: string;
    Timeout: number;
    DefaultMethod: string;
    ContentType: string;
  };
};

export interface ApiDataType {
  Path: string;
  DefaultValue: string;
  DataType: "string" | "int" | "float" | "boolean" | "array" | "object";
  Name: string;
  IsArray: boolean;
  RawJson: string;
}

export interface ApiDataCollectionType {
  Uuid: string;
  Label: string;
  Path: string;
  Fields: ApiDataType[];
  RawJson: string;
  ApiProfiles: { Uuid: string; BaseUrl: string }[];
}

export type SolutionEvent = {
  SolutionClientUuid: string;
  Type: string;
  Name: string;
  Description: string;
  Reference: string;
  StartedAt?: string;
  EndedAt?: string;
  Severity: string;
  AddTicket: boolean;

  PreferredChannels: string[];
  Contacts: string[];
  PhoneNumbers: string[];
  Emails: string[];
  Assets: string[];
  Areas: any[];

  Email: string;
  ClientId: number;

  EditorMessage: any;

  ContactsAndAssetsCriteria: AssetsMapRequestType;
};

export type SolutionClientType = {
  Uuid: string;
  SolutionUuid: string;
  ClientId: number;
  CreatedAt: number;
  Email: string;
  Status: string;
  Sources: any[];
  LatestSource: any;
  WasDeployed: boolean;
};

export type ScheduleType = {
  // Identity
  Uuid: string | undefined; // Omit or "" to create; existing UUID to update
  Label: string; // 0 Maximum // Require number of successful dispatches. 0 = unlimited. Once SuccessfulRuns
  Description: string;
  // Target — exactly one of these must be set
  ProjectUuid?: string | undefined;
  Project?: {
    ID: number;
    Label: string;
    Description: string;
  };
  BroadcasterUuid?: string | undefined;
  Broadcaster?: {
    Uuid: string;
    Label: string;
    Description: string;
  };
  // Broadcaster recipient overrides (optional, only relevant when BroadcasterUuid is set)
  BroadcastSendList: string;
  BroadcastContactUuids: string[];
  BroadcastGroups: string[];
  // Project inputs (optional, only relevant when ProjectUuid is set)
  TriggerData: { param: string[] };
  // Activation
  IsActive: boolean;
  // Date range (ISO 8601: "2026-01-15T09:00:00Z" or "2026-01-15")
  StartDate: string;
  EndDate: string | undefined;
  // Run limiting (0 = unlimited)
  MaxRuns: number;
  // Frequency — required fields depend on FrequencyKind
  NextRun: string | undefined; // ISO 8601 date string of next scheduled run; null if not scheduled to run again
  FrequencyKind: "interval" | "fixed"; // "interval" or "fixed" — Required
  IntervalValue: number;
  IntervalUnit: "minute" | "hour" | "day";
  IntervalRunAtHour: number;
  IntervalRunAtMinute: number;
  WeekDays: number[]; // Mon–Fri only; empty = every day
  // Exception dates (replaces existing list on update)
  ExceptionDates: string[]; //"2026-07-04", "2026-12-24->2026-12-31"
  Hours: number[];
  Minutes: number[];
  TimeZone: string;
  IsAdvanced: boolean;
};

export type AssetsStatsType = {
  TotalAssets: number;
  AssetsByKind: {
    [kind: string]: number;
  };
  TotalLocations: number;
  TotalAssociatedContacts: number;
  TotalChildAssets: number;
  TotalParentAssets: number;
};

export type ProcessStatsType = {
  Channel: string;
  Delivered: number;
  Dispatched: number;
  Failed: number;
  Replies: number;
  FormAccessed: number;
  FormSubmitted: number;
}[];

export type ComponentsStatsType = {
  projects: any[];
  total: number;
};

// localhost:8000
export const settingsAPI = createApi({
  reducerPath: "settingsAPI",
  baseQuery: baseQueryWithErrorHandling({
    prepareHeaders: (headers: Headers): Headers => {
      if (typeof csrfToken == "string") {
        headers.set("X-CSRF-TOKEN", csrfToken);
      }
      headers.set("Accept", "application/json; charset=utf-8");
      headers.set("Content-Type", "application/json; charset=utf-8");
      return headers;
    },
  }),
  refetchOnMountOrArgChange: true,
  tagTypes: ["Users", "Roles"],
  endpoints: (builder) => ({
    getUsersSettingsData: builder.query<
      { rows: any[]; total: number },
      { limit: number; offset: number; order: "asc" | "desc" }
    >({
      query: (params) => {
        return {
          url: `/admin/users/get_table`,
          method: "GET",
          params,
        };
      },
      providesTags: ["Users"],
    }),
    getUsersSettingsFilterData: builder.query<any, void>({
      query: () => {
        return {
          url: `/admin/get-accounts`,
          method: "GET",
        };
      },
    }),
    getUsersAccounts: builder.query<any, void>({
      query: () => {
        return {
          url: `/admin/get-accounts`,
          method: "GET",
        };
      },
    }),
    getPersonalData: builder.query<any, void>({
      query: () => {
        return {
          url: `/profile-data`,
          method: "GET",
        };
      },
    }),
    getUsersSettingsProductsFilterData: builder.query<any, void>({
      query: () => {
        return {
          url: `/admin/get-accounts`,
          method: "GET",
        };
      },
    }),
    getNewUserData: builder.query<any, void>({
      query: () => {
        return {
          url: `/admin/user/form`,
          method: "GET",
        };
      },
    }),
    getUserData: builder.query<any, number>({
      query: (id) => {
        return {
          url: `/admin/user/data/${id}`,
          method: "GET",
        };
      },
      providesTags: ["Users"],
    }),

    // User roles
    getUserRolesTableData: builder.query<
      any,
      { order: "asc" | "desc"; offset: number; limit: number; search: string }
    >({
      query: (params) => {
        return {
          url: "/admin/roles/table-paginated",
          method: "GET",
          params,
        };
      },
      providesTags: ["Roles"],
    }),
    getUserRolesEditData: builder.query<any, { roleId: number }>({
      query: (roleId) => {
        return {
          url: `/admin/roles/edit-data/${roleId}`,
          method: "GET",
        };
      },
      providesTags: ["Roles"],
    }),
    getUserRolesCreateData: builder.query<any, void>({
      query: () => {
        return {
          url: `/admin/roles/create-data`,
          method: "GET",
        };
      },
      providesTags: ["Roles"],
    }),
    updateUserRolesData: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/admin/roles/update`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Users", "Roles"],
    }),
    storeUserRolesData: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/admin/roles/store`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Users", "Roles"],
    }),
    deleteUserRolesData: builder.mutation<any, number>({
      query: (roleId) => {
        return {
          url: `/admin/roles/delete/${roleId}`,
          method: "POST",
        };
      },
      invalidatesTags: ["Users", "Roles"],
    }),
    updateUserData: builder.mutation<any, { url: string; body: any }>({
      query: ({ url, body }) => {
        return {
          url,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Users"],
    }),
    updatePersonalData: builder.mutation<any, { body: any }>({
      query: ({ body }) => {
        return {
          url: "/profile/update/v2",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Users"],
    }),
    getTotpSetupData: builder.query<any, void>({
      query: () => ({ url: "/profile/authenticator/setup-data" }),
    }),
    enableTotp: builder.mutation<any, { code: string }>({
      query: ({ code }) => ({
        url: "/profile/authenticator/enable",
        method: "POST",
        body: { code },
      }),
      invalidatesTags: ["Users"],
    }),
    disableTotp: builder.mutation<any, { code: string }>({
      query: ({ code }) => ({
        url: "/profile/authenticator/disable",
        method: "POST",
        body: { code },
      }),
      invalidatesTags: ["Users"],
    }),
    updateUserLocked: builder.mutation<
      any,
      { userId: number; locked: boolean }
    >({
      query: ({ userId, locked }) => `/user/locked/${userId}?locked=${locked}`,
      invalidatesTags: ["Users"],
    }),
    deleteUser: builder.mutation<
      any,
      { userId: number; body: { deleteAsscContact: boolean } }
    >({
      query: ({ userId, body }) => ({
        url: `/admin/user/delete/${userId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    updateBillingSettingsData: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/billing/details/update/${body.id}`,
          method: "POST",
          body,
        };
      },
    }),
    updateAccountSettingsData: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/system/settings/system_wide_settings`,
          method: "POST",
          body,
        };
      },
    }),
    updateSecuritySettingsData: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/logic/organisation-settings/security/update`,
          method: "POST",
          body,
        };
      },
    }),
    requestNewHeader: builder.mutation<any, string>({
      query: (customHeader) => ({
        url: `/custom-header-request`,
        method: "POST",
        body: { customHeader },
      }),
    }),
    updateOrganisationSettingsData: builder.mutation<
      any,
      OrganisationDetailsType
    >({
      query: (body) => {
        console.log(body);
        return {
          url: `/organisation-settings/update/${body.customers.id}`,
          method: "POST",
          body,
        };
      },
    }),
    clearCompanyLogo: builder.mutation<any, any>({
      query: () => ({
        url: `/fileupload/companylogo/clear`,
        method: "POST",
      }),
    }),
    changeProduct: builder.mutation<any, { accountId: number }>({
      query: ({ accountId }) => ({
        url: `/changeproduct/${accountId}`,
        method: "GET",
      }),
    }),

    getTncCurrentMsa: builder.query<any, void>({
      query: () => `/tnc-current-msa`,
    }),
    // getActiveUsersWidgetData: builder.query<ActiveUsersWidgetDataType, void>({
    //   query: () => `/logic/active-users`,
    // }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUsersSettingsProductsFilterDataQuery,
  useGetUsersSettingsDataQuery,
  useGetUsersSettingsFilterDataQuery,
  useGetNewUserDataQuery,
  useUpdateBillingSettingsDataMutation,
  useUpdateSecuritySettingsDataMutation,
  useRequestNewHeaderMutation,
  useUpdateOrganisationSettingsDataMutation,
  useUpdateAccountSettingsDataMutation,
  useUpdateUserDataMutation,
  useUpdateUserLockedMutation,
  useDeleteUserMutation,
  useGetUserDataQuery,
  useLazyGetUserDataQuery,
  useLazyGetNewUserDataQuery,
  useGetPersonalDataQuery,
  useUpdatePersonalDataMutation,
  useGetTotpSetupDataQuery,
  useEnableTotpMutation,
  useDisableTotpMutation,
  useClearCompanyLogoMutation,
  useGetUserRolesTableDataQuery,
  useGetUserRolesEditDataQuery,
  useLazyGetUserRolesEditDataQuery,
  useUpdateUserRolesDataMutation,
  useDeleteUserRolesDataMutation,
  useStoreUserRolesDataMutation,
  useGetUserRolesCreateDataQuery,
  useLazyGetUserRolesCreateDataQuery,
  useGetUsersAccountsQuery,
  useChangeProductMutation,
  useGetTncCurrentMsaQuery,
} = settingsAPI;

//localhost:3100 proxy: "logic/workflows/api" | "contacts/v1" | "logic/reporting"
export const settingsProxyAPI = createApi({
  reducerPath: "settingsProxyAPI",
  baseQuery: baseProxyQueryWithReauth("logic/workflows/api"),
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "ApiConnections",
    "ApiDataCollections",
    "DataEntryStructures",
    "ProjectInterfaces",
    "Solutions",
    "Components",
    "SolutionEvents",
    "EventSolutions",
    "Events",
    "Tags",
    "Schedulers",
    "SendListInfo",
  ],

  endpoints: (builder) => ({
    getBillingSettingsData: builder.query<
      {
        company_name: string;
        invoicing_address: string;
        company_num: number;
        vat_number: number;
        account_name: string;
        account_email: string;
      },
      void
    >({
      query: () => `/settings/billing`,
    }),

    getHeaderSettingsData: builder.query<
      {
        allowed: {
          name: string;
          approved: number;
          whoApproved: string;
          clientId: number;
        }[];
        canRequestDynamicHeaders: boolean;
        restrictDynamicHeaders: boolean;
      },
      void
    >({
      query: () => `/settings/dynamic-headers`,
    }),
    getCommsSettingsData: builder.query<CommsSettingsDataType, void>({
      query: () => `/settings/comms-defaults`,
    }),
    updateCommsSettingsData: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/settings/comms-defaults`,
          method: "POST",
          body,
        };
      },
    }),

    getSecuritySettingsData: builder.query<
      {
        system_timeout: number;
        password_change: number;
        login_attempts: number;
        two_factors: "Y" | "N";
        tf_code_attempts: number;
        tf_expire_minutes: number;
        tf_frequency: string;
        force_authenticator: "Y" | "N";
      },
      void
    >({
      query: () => `/settings/security`,
    }),

    getOrganisationSettingsData: builder.query<OrganisationDetailsType, void>({
      query: () => `/settings/organisation`,
    }),

    getAccountSettingsData: builder.query<
      {
        client_id: number;
        country_id: number;
        logo: string;
        name: string;
        timezone: string;
      },
      void
    >({
      query: () => `/settings/account`,
    }),
    getApiConnections: builder.query<
      {
        connections: ApiConnectionType[];
        total: number;
      },
      {
        status: string;
        kind: string;
        orderBy: string;
        search: string;
        offset: number;
        limit: number;
      }
    >({
      query: (params) => ({ url: `/api-connections`, method: "GET", params }),
      providesTags: ["ApiConnections"],
    }),
    getApiConnection: builder.query<ApiConnectionType, string>({
      query: (uuid) => `/api-connection/${uuid}`,
      providesTags: ["ApiConnections"],
    }),
    updateApiConnection: builder.mutation<any, Partial<ApiConnectionType>>({
      query: (body) => ({
        url: `/api-connection`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ApiConnections"],
    }),

    getSystemDataStructures: builder.query<any, void>({
      query: () => ({
        url: `/system-data-structures`,
        method: "GET",
      }),
    }),

    getApiDataCollections: builder.query<
      {
        collections: ApiDataCollectionType[];
        total: number;
      },
      {
        search: string;
        uuid?: string;
        offset: number;
        limit: number;
      }
    >({
      query: (params) => ({
        url: `/json-api-data-collections`,
        method: "GET",
        params,
      }),
      providesTags: ["ApiDataCollections"],
    }),

    getApiDataCollection: builder.query<ApiDataCollectionType, string>({
      query: (uuid) => `/json-api-data-collection/${uuid}`,
      providesTags: ["ApiDataCollections"],
    }),

    deleteApiDataCollection: builder.mutation<any, string>({
      query: (uuid) => ({
        url: `/json-api-data-collection/${uuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ApiDataCollections"],
    }),

    saveApiDataCollection: builder.mutation<any, ApiDataCollectionType>({
      query: (body) => ({
        url: `/json-api-data-collection`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ApiDataCollections"],
    }),

    getApiLogs: builder.query<
      any,
      { Uuid: string; offset: number; limit: number }
    >({
      query: ({ Uuid, offset, limit }) => ({
        method: "GET",
        url: `logs`,
        params: {
          resource: Uuid,
          user: pageProps.user.email,
          offset,
          limit,
          kind: ["api_connection", "api_request"],
          level: "info",
        },
      }),
    }),
    resetMessagingApiPassword: builder.query<
      any,
      { apiConnectionUuid: string }
    >({
      query: ({ apiConnectionUuid }) => ({
        method: "GET",
        url: `api-connection/api-reset-password/${apiConnectionUuid}`,
      }),
    }),
    resetApiToken: builder.query<any, { Uuid: string }>({
      query: ({ Uuid }) => ({
        method: "GET",
        url: `api-connections-new-token/${Uuid}`,
      }),
    }),
    getNylasStatus: builder.query<
      {
        status: "connected" | "connecting" | "not-connected" | "failed";
      },
      { CONNECTION_UUID: string; NYLAS_EMAIL: string }
    >({
      query: ({ CONNECTION_UUID, NYLAS_EMAIL }) => ({
        url: `api-connection/nylas-status/${CONNECTION_UUID}?email=${NYLAS_EMAIL}`,
        method: "GET",
      }),
    }),

    getDataEntryStructure: builder.query<ApiDataCollectionType, string>({
      query: (uuid) => `/data-entry-structures/${uuid}`,
      providesTags: ["DataEntryStructures"],
    }),

    getLogs: builder.query<
      any,
      {
        resource?: string;
        offset: number;
        limit: number;
        kind?: ("api_connection" | "api_request" | "monitor")[];
        level?: "info" | "warning" | "error";
      }
    >({
      query: (params) => ({
        method: "GET",
        url: `logs`,
        params,
      }),
    }),
    getProjectInterfaces: builder.query<
      {
        interface: {
          [key: string]: {
            Order: number;
            Name: string;
            Description: string;
            Required: boolean;
            Format: string;
          };
        };
        projects: any[];
        total: number;
      },
      { for: "ping-monitor" | "solution"; role?: string[] }
    >({
      query: (params) => ({
        url: `list-project-interfaces`,
        method: "GET",
        params,
      }),
      providesTags: ["ProjectInterfaces"],
    }),

    postAiPrompt: builder.mutation<
      any,
      {
        prompt: string;
        sentences: string[];
      }
    >({
      query: (body) => ({
        url: `/ai/prompt `,
        method: "POST",
        body,
        hideToasts: true,
      }),
    }),
    getProjectHttpInfo: builder.query<
      {
        Uuid: string;
        Label: string;
        Description: string;
        Reference: string;
        Example: {
          [key: string]: any;
        };
        Variables: [
          {
            Field: string;
            Label: string;
            Format: string;
            Required: boolean;
            Multiple: boolean;
          }
        ];
      },
      { uuid: string }
    >({
      query: ({ uuid }) => ({
        url: `/http-info/${uuid}`,
        method: "GET",
      }),
      // providesTags: ["ProjectInterfaces"],
    }),
    exportProject: builder.query<
      any,
      { projectId: number; isSameAccount: boolean }
    >({
      query: ({ projectId, isSameAccount = true }) => ({
        url: `/export-project/${projectId}?sameAccount=${isSameAccount}`,
        method: "GET",
      }),
    }),
    importProject: builder.mutation<any, string>({
      query: (file) => {
        return {
          url: `/import-project`,
          method: "POST",
          body: file,
        };
      },
    }),
    getSolutions: builder.query<
      any,
      {
        limit?: number;
        offset?: number;
        status?: string[];
        kind?: string[];
        search?: string;
        purchased?: "yes" | "no";
        deployed?: "yes" | "no";
      }
    >({
      query: (params) => ({
        url: `/solutions`,
        method: "GET",
        params,
      }),
      providesTags: ["Solutions"],
    }),
    getSolutionByUuid: builder.query<any, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/solutions/${uuid}`,
        method: "GET",
      }),
      providesTags: ["Solutions"],
    }),
    getSolutionForDeploymentByUuid: builder.query<any, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/solution-for-deployment/${uuid}`,
        method: "GET",
      }),
      providesTags: ["Solutions"],
    }),
    getImportedSolutionInfoByUuid: builder.query<any, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/solutions/imported-info/${uuid}`,
        method: "GET",
      }),
    }),
    resetImportedSolutionInfoByUuid: builder.mutation<any, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/solutions/imported/remove-projects/${uuid}`,
        method: "GET",
      }),
      invalidatesTags: ["Solutions"],
    }),
    setImportedSolutionVariableDefaults: builder.mutation<
      any,
      {
        uuid: string;
        body: { [key: string]: any };
      }
    >({
      query: ({ uuid, body }) => {
        return {
          url: `/solutions/imported/project-defaults/${uuid}`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Solutions"],
    }),
    createSolution: builder.mutation<
      any,
      {
        Uuid?: string;
        Label: string;
        Description: string;
        Status: string;
        Reference: string;
        Version: string;
        Kind: string;
      }
    >({
      query: (body) => {
        return {
          url: body.Uuid ? `/solutions/${body.Uuid}` : `/solutions`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Solutions"],
    }),
    addProjectToSolution: builder.mutation<
      any,
      { SolutionUuid: string; ProjectUuid: string }
    >({
      query: (body) => {
        return {
          url: `/solutions/add-project`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Solutions"],
    }),
    removeProjectFromSolution: builder.mutation<
      any,
      { SolutionUuid: string; ComponentUuid: string }
    >({
      query: (body) => {
        return {
          url: `/solutions/remove-project`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Solutions"],
    }),
    listProject: builder.query<
      any,
      {
        offset: number;
        limit: number;
        status: string;
        for: string;
        role: string;
        search: string;
        solutionFilter: string;
        order: string;
        direction: "asc" | "desc";
      }
    >({
      query: (params) => ({
        url: `/list-projects`,
        method: "GET",
        params,
      }),
      providesTags: ["Components"],
    }),
    deleteProject: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/delete-project/${id}`,
        method: "GET",
      }),
      invalidatesTags: ["Components"],
    }),
    purchaseSolution: builder.mutation<any, { SolutionUuid: string }>({
      query: (body) => {
        return {
          url: `/solutions/purchase`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Solutions"],
    }),
    deploySolution: builder.mutation<
      any,
      {
        SolutionClientUuid: string;
        References: {
          [key: string]: { [key: string]: { [key: string]: string } };
        };
      }
    >({
      query: (body) => {
        return {
          url: `/solutions/deploy`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Solutions"],
    }),
    getEventSolutions: builder.query<SolutionClientType[], void>({
      query: () => ({
        url: `/solutions/settings/events`,
        method: "GET",
      }),
      providesTags: ["EventSolutions"],
    }),
    getEventAssetsSolutions: builder.query<
      { assets: { AssetUuid: string }[]; total: number },
      {
        uuid: string;
        page?: number;
        perPage?: number;
        search?: string;
        kind?: string;
      }
    >({
      query: ({ uuid, ...params }) => ({
        url: `/event/assets/${uuid}`,
        method: "GET",
        params,
      }),
      providesTags: ["EventSolutions"],
    }),
    createSolutionEvent: builder.mutation<any, Partial<SolutionEvent>>({
      query: (body) => {
        return {
          url: `/solutions/events/event-create`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    createNoSolutionEvent: builder.mutation<any, Partial<SolutionEvent>>({
      query: (body) => {
        return {
          url: `/no-solutions/events/event-create`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    addTicketToEvent: builder.mutation<
      any,
      {
        SolutionClientUuid: string;
        ID: string;
        Ticket: string;
        Description: string;
      }
    >({
      query: (body) => {
        return {
          url: `/solutions/events/add-ticket`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    updateSolutionEvent: builder.mutation<
      any,
      {
        SolutionClientUuid: string;
        ID: string;
        Severity: string;
      }
    >({
      query: (body) => {
        return {
          url: `/solutions/events/update-event`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    updateNoSolutionEvent: builder.mutation<
      any,
      {
        SolutionClientUuid: string;
        ID: string;
        Severity: string;
      }
    >({
      query: (body) => {
        return {
          url: `/no-solutions/events/update-event`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    createUpdateEvent: builder.mutation<
      any,
      {
        SolutionClientUuid: string;
        ID: string;
        Message: string;
      }
    >({
      query: (body) => {
        return {
          url: `/solutions/events/create-update`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    createCommunicationEvent: builder.mutation<
      any,
      {
        SolutionClientUuid: string;
        ID: string;
        Message: string;
        PreferredChannels: ("email" | "sms")[];
      }
    >({
      query: (body) => {
        return {
          url: `/solutions/events/create-communication`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    closeEvent: builder.mutation<
      any,
      {
        SolutionClientUuid: string;
        ID: string;
        Message: string;
      }
    >({
      query: (body) => {
        return {
          url: `/solutions/events/close-event`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    closeNoSolutionEvent: builder.mutation<
      any,
      {
        SolutionClientUuid: string;
        ID: string;
        Message: string;
      }
    >({
      query: (body) => {
        return {
          url: `/no-solutions/events/close-event`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SolutionEvents", "Events"],
    }),
    getEvents: builder.query<
      { events: EventType[]; total: number },
      { page?: number; perPage?: number; assets?: string[]; status?: string }
    >({
      query: (params) => ({
        url: `/events`,
        params,
      }),
      providesTags: ["Events"],
    }),
    getEvent: builder.query<EventType, string>({
      query: (uuid) => ({
        url: `/event/${uuid}`,
      }),
      providesTags: ["Events"],
    }),
    updateEvent: builder.mutation<any, EventType>({
      query: (body) => {
        return {
          url: `/events`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Events"],
    }),
    updateEventStatus: builder.mutation<any, { uuid: string; status: string }>({
      query: ({ uuid, status }) => {
        return {
          url: `/event-status/${uuid}`,
          method: "POST",
          body: { status },
        };
      },
      invalidatesTags: ["Events"],
    }),
    addAssetsToEvent: builder.mutation<any, { uuid: string; assets: string[] }>(
      {
        query: ({ uuid, assets }) => ({
          url: `/event/assets/add`,
          method: "POST",
          body: { EventUuid: uuid, Add: assets },
        }),
        invalidatesTags: ["Events"],
      }
    ),
    replaceAssetsToEvent: builder.mutation<
      any,
      { uuid: string; assets: string[] }
    >({
      query: ({ uuid, assets }) => ({
        url: `/event/assets/replace`,
        method: "POST",
        body: { EventUuid: uuid, Replace: assets },
      }),
      invalidatesTags: ["Events"],
    }),
    removeAssetsToEvent: builder.mutation<
      any,
      { uuid: string; assets: string[] }
    >({
      query: ({ uuid, assets }) => ({
        url: `/event/assets/remove`,
        method: "POST",
        body: { EventUuid: uuid, Remove: assets },
      }),
      invalidatesTags: ["Events"],
    }),

    deleteEvent: builder.mutation<any, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/event/rules/${uuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Events"],
    }),
    getEventContactsViaAssets: builder.query<
      { contacts: { ContactUuid: string; Email: string }[]; total: number },
      { uuid: string }
    >({
      query: (uuid) => ({
        url: `/event-contacts-via-asset/${uuid}`,
        method: "GET",
      }),
      providesTags: ["Events"],
    }),
    getTags: builder.query<
      { tags: { TagUuid: string; Name: string }[] },
      {
        kind: "category" | "industry";
        search?: string;
        limit: number;
        offset: number;
      }
    >({
      query: (params) => ({
        url: `/tags`,
        method: "GET",
        params,
      }),
      providesTags: ["Tags"],
    }),
    getSolutionsByTag: builder.query<
      any,
      {
        kind: "category" | "industry";
        category: string;
        purchased: "yes" | "no";
        offset: number;
        limit: number;
      }
    >({
      query: (params) => ({
        url: `/solutions-tags-summary`,
        method: "GET",
        params,
      }),
      providesTags: ["Solutions"],
    }),
    getWeatherDisplayMapColours: builder.query<any, void>({
      query: () => ({
        url: `/weather-display/map-colours`,
        method: "GET",
      }),
    }),
    createWeatherDisplayMapColours: builder.mutation<
      any,
      { Colour: string; To: string; From: string; Condition: string }[]
    >({
      query: (body) => ({
        url: `/weather-display/map-colours`,
        method: "POST",
        body,
      }),
    }),
    getClientUnits: builder.query<any, void>({
      query: () => ({
        url: `/weather-display/client-units`,
        method: "GET",
      }),
    }),
    updateClientUnits: builder.mutation<any, { units: string }>({
      query: (body) => ({
        url: `/weather-display/client-units`,
        method: "POST",
        body,
      }),
    }),
    createOrUpdateSchedule: builder.mutation<any, ScheduleType>({
      query: (body) => ({
        url: `component-scheduling`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Schedulers"],
    }),
    getSchedulers: builder.query<
      { schedules: ScheduleType[]; total: number },
      {
        page: number;
        per_page: number;
        project_uuid?: string;
        broadcaster_uuid?: string;
        type?: "broadcaster" | "project";
        is_active?: boolean;
        search?: string;
      }
    >({
      query: (params) => ({
        url: `component-scheduling`,
        method: "GET",
        params,
      }),
      providesTags: ["Schedulers"],
    }),
    getSchedulerByUuid: builder.query<ScheduleType, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `component-scheduling/${uuid}`,
        method: "GET",
      }),
      providesTags: ["Schedulers"],
    }),
    deleteSchedule: builder.mutation<any, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `component-scheduling/${uuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Schedulers"],
    }),
    updateSchedulerStatus: builder.mutation<
      any,
      { uuid: string; isActive: boolean }
    >({
      query: ({ uuid, isActive }) => ({
        url: `component-scheduling/${uuid}/status`,
        method: "PATCH",
        body: { is_active: isActive },
      }),
      invalidatesTags: ["Schedulers"],
    }),
    getSendListInfo: builder.query<
      {
        [uuid: string]: {
          Kind: "workflow" | "message_template";
          ID: number;
          Uuid: string;
          Label: string;
          ProjectID: number;
          ProjectUuid: string;
          ProjectLabel: string;
        };
      },
      { uuid: string[] }
    >({
      query: (params) => ({
        url: `/send-list-info`,
        method: "GET",
        params,
      }),
      providesTags: ["SendListInfo"],
    }),
    getAssetsStats: builder.query<AssetsStatsType, void>({
      query: () => ({
        url: `/stats/assets`,
        method: "GET",
      }),
    }),
    getComponentsStats: builder.query<
      ComponentsStatsType,
      { started?: string; project?: string }
    >({
      query: (params) => ({
        url: `/list-projects`,
        method: "GET",
        params,
      }),
    }),
    getProcessStats: builder.query<
      ProcessStatsType,
      { started?: string; project?: string; process?: string }
    >({
      query: (params) => ({
        url: `/process-stats`,
        method: "GET",
        params,
      }),
    }),
    getBroadcasters: builder.query<{ data: any[]; total: number }, void>({
      query: () => ({
        url: `/broadcasters`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetBillingSettingsDataQuery,
  useGetCommsSettingsDataQuery,
  useUpdateCommsSettingsDataMutation,
  useGetHeaderSettingsDataQuery,
  useGetSecuritySettingsDataQuery,
  useGetOrganisationSettingsDataQuery,
  useGetAccountSettingsDataQuery,
  useGetApiConnectionsQuery,
  useGetApiConnectionQuery,
  useLazyGetApiConnectionQuery,
  useUpdateApiConnectionMutation,
  useGetNylasStatusQuery,
  useGetApiLogsQuery,
  useLazyResetApiTokenQuery,
  useGetApiDataCollectionsQuery,
  useGetApiDataCollectionQuery,
  useDeleteApiDataCollectionMutation,
  useSaveApiDataCollectionMutation,
  useGetSystemDataStructuresQuery,
  useGetDataEntryStructureQuery,
  useGetLogsQuery,
  useGetProjectInterfacesQuery,
  usePostAiPromptMutation,
  useLazyResetMessagingApiPasswordQuery,
  useGetProjectHttpInfoQuery,
  useLazyGetProjectHttpInfoQuery,
  useExportProjectQuery,
  useLazyExportProjectQuery,
  useImportProjectMutation,
  useGetSolutionsQuery,
  useCreateSolutionMutation,
  useAddProjectToSolutionMutation,
  useRemoveProjectFromSolutionMutation,
  useListProjectQuery,
  useGetSolutionByUuidQuery,
  usePurchaseSolutionMutation,
  useDeploySolutionMutation,
  useLazyGetSolutionByUuidQuery,
  useGetEventSolutionsQuery,
  useCreateSolutionEventMutation,
  useAddTicketToEventMutation,
  useCreateUpdateEventMutation,
  useCreateCommunicationEventMutation,
  useCloseEventMutation,
  useGetEventsQuery,
  useGetEventQuery,
  useUpdateEventMutation,
  useUpdateEventStatusMutation,
  useAddAssetsToEventMutation,
  useReplaceAssetsToEventMutation,
  useRemoveAssetsToEventMutation,
  useDeleteEventMutation,
  useUpdateSolutionEventMutation,
  useDeleteProjectMutation,
  useGetImportedSolutionInfoByUuidQuery,
  useSetImportedSolutionVariableDefaultsMutation,
  useResetImportedSolutionInfoByUuidMutation,
  useGetEventAssetsSolutionsQuery,
  useGetEventContactsViaAssetsQuery,
  useGetSolutionForDeploymentByUuidQuery,
  useGetTagsQuery,
  useGetSolutionsByTagQuery,
  useGetWeatherDisplayMapColoursQuery,
  useCreateWeatherDisplayMapColoursMutation,
  useGetClientUnitsQuery,
  useUpdateClientUnitsMutation,
  useCreateOrUpdateScheduleMutation,
  useGetSchedulersQuery,
  useGetSchedulerByUuidQuery,
  useDeleteScheduleMutation,
  useUpdateSchedulerStatusMutation,
  useGetSendListInfoQuery,
  useLazyGetSendListInfoQuery,
  useGetAssetsStatsQuery,
  useGetComponentsStatsQuery,
  useGetProcessStatsQuery,
  useGetBroadcastersQuery,
  useCreateNoSolutionEventMutation,
  useUpdateNoSolutionEventMutation,
  useCloseNoSolutionEventMutation,
  useLazyGetEventsQuery,
} = settingsProxyAPI;
